import React, { useState } from 'react';
import { Box, Dialog, IconButton, Typography, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

interface ImagePreviewProps {
  src: string | null | undefined;
  alt: string;
  label?: string;
  width?: number | string;
  height?: number | string;
  shape?: 'rect' | 'circle';
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ 
  src, 
  alt, 
  label, 
  width = 100, 
  height = 100,
  shape = 'rect' 
}) => {
  const [open, setOpen] = useState(false);

  // Helper to handle relative URLs
  const getFullUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;
    if (url.startsWith('http')) return url;
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    return `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`;
  };

  const fullSrc = src ? getFullUrl(src) : '';

  if (!fullSrc) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {label && <Typography variant="caption" color="textSecondary">{label}</Typography>}
            <Box 
                sx={{ 
                    width, 
                    height, 
                    borderRadius: shape === 'circle' ? '50%' : 1, 
                    bgcolor: '#f5f5f5', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    border: '1px dashed #ccc'
                }}
            >
                <CameraAltIcon color="disabled" />
            </Box>
        </Box>
      );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
       {label && <Typography variant="caption" color="textSecondary">{label}</Typography>}
       <Box 
         sx={{ 
           width, 
           height, 
           border: '1px solid #ddd', 
           borderRadius: shape === 'circle' ? '50%' : 1, 
           overflow: 'hidden', 
           cursor: 'pointer',
           position: 'relative',
           '&:hover .overlay': { opacity: 1 }
         }}
         onClick={() => setOpen(true)}
       >
         <img src={fullSrc} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
         <Box 
            className="overlay"
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s'
            }}
         >
             <VisibilityIcon sx={{ color: 'white' }} />
         </Box>
       </Box>

       <Dialog 
         open={open} 
         onClose={() => setOpen(false)} 
         maxWidth="md"
         PaperProps={{
             sx: { bgcolor: 'transparent', boxShadow: 'none' }
         }}
       >
         <Box sx={{ position: 'relative', bgcolor: 'black', borderRadius: 1, overflow: 'hidden' }}>
            <IconButton 
                onClick={() => setOpen(false)} 
                sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
            >
                <CloseIcon />
            </IconButton>
            <img src={fullSrc} alt={alt} style={{ maxWidth: '100%', maxHeight: '85vh', display: 'block' }} />
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'rgba(0,0,0,0.8)', color: 'white' }}>
                 <Typography variant="subtitle1">{alt}</Typography>
                 <Button 
                    startIcon={<DownloadIcon />} 
                    href={fullSrc} 
                    download 
                    target="_blank"
                    sx={{ color: 'white', borderColor: 'white' }}
                    variant="outlined"
                    size="small"
                 >
                    Download
                 </Button>
            </Box>
         </Box>
       </Dialog>
    </Box>
  );
};

export default ImagePreview;
